import Vue from 'vue'
import Vuex from 'vuex'
import i18n from '@/i18n'

Vue.use(Vuex)

const mutations = {
  setUser (state, { user }) {
    state.user = user
  },
  registerLink (state, { titleFunc, group, href, action }) {
    if (!state.links[group]) {
      Vue.set(state.links, group, {})
    }
    Object.defineProperty(state.links[group], href, {
      configurable: true,
      enumerable: true,
      get: titleFunc
    })
    Vue.set(state.actions, href, action)
  },
  unregisterLink (state, { group, href }) {
    Vue.delete(state.links[group], href)
    Vue.delete(state.actions, href)
  },
  unregisterGroup (state, { group }) {
    for (const href in state.links[group]) {
      Vue.delete(state.actions, href)
    }
    Vue.delete(state.links, group)
  },
  setEditMode (state, { enabled }) {
    state.editMode = Boolean(enabled)
  },
  setEditAllowed (state, { allowed }) {
    state.editAllowed = Boolean(allowed)
  },
  setProject (state, { project }) {
    state.project = project
  },
  toggleAssetSelected (state, { path }) {
    if (state.selectedAssets[path]) {
      Vue.delete(state.selectedAssets, path)
    } else {
      Vue.set(state.selectedAssets, path, true)
    }
  },
  deselectAllAssets (state) {
    state.selectedAssets = {}
  },
  setMediaState (state, { path, playingState }) {
    if (playingState === undefined) {
      Vue.delete(state.media, path)
    } else {
      Vue.set(state.media, path, playingState)
    }
  },
  setContentLocale (state, { locale }) {
    state.contentLocale = locale
  },
  registerFont (state, { path, fontFamily }) {
    if (!state.registeredFonts[fontFamily]) {
      Vue.set(state.registeredFonts, fontFamily, [path])
    } else if (!state.registeredFonts[fontFamily].includes(path)) {
      state.registeredFonts[fontFamily].push(path)
    }
  },
  unregisterFont (state, { path, fontFamily }) {
    const index = (state.registeredFonts[fontFamily] || []).indexOf(path)
    if (index > -1) {
      Vue.delete(state.registeredFonts[fontFamily], index)
    }
    if (state.registeredFonts[fontFamily].length < 1) {
      Vue.delete(state.registeredFonts, fontFamily)
      return true
    } else {
      return false
    }
  },
  addFontFace (state, { fontFamily, fontFace }) {
    Vue.set(state.fontFaces, fontFamily, fontFace)
  },
  removeFontFace (state, { fontFamily }) {
    Vue.delete(state.fontFaces, fontFamily)
  }
}

const actions = {
  setUser ({ commit }, payload) {
    commit('setUser', payload)
  },
  registerLink: ({ commit }, { titleFunc, group, href, action }) => {
    commit('registerLink', { titleFunc, group, href, action })
  },
  unregisterLink: ({ commit }, { group, href }) => {
    commit('unregisterLink', { group, href })
  },
  unregisterGroup ({ commit }, { group }) {
    commit('unregisterGroup', { group })
  },
  toggleEditMode ({ commit, state }) {
    if (state.editAllowed) {
      commit('setEditMode', { enabled: !state.editMode })
    } else {
      commit('setEditMode', { enabled: false })
    }
  },
  setEditMode ({ commit }, { enabled }) {
    commit('setEditMode', { enabled })
  },
  setEditAllowed ({ commit }, { allowed }) {
    commit('setEditAllowed', { allowed })
  },
  setProject ({ commit }, payload) {
    commit('setProject', payload)
  },
  toggleAssetSelected ({ commit }, { path }) {
    commit('toggleAssetSelected', { path })
  },
  deselectAllAssets ({ commit }) {
    commit('deselectAllAssets')
  },
  setUiLocale ({ commit }, { locale }) {
    i18n.locale = locale
  },
  setContentLocale ({ commit }, { locale }) {
    commit('setContentLocale', { locale })
  },
  async registerFont ({ commit, state }, { path, fontFamily }) {
    if (!path) {
      throw new Error('Can\'t register font. Path has to be provided.')
    }
    commit('registerFont', { path, fontFamily })
    if (!state.fontFaces[fontFamily]) {
      const fontFace = new FontFace(fontFamily, `url(${fontFamily})`)
      commit('addFontFace', { fontFamily, fontFace })
      document.fonts.add(fontFace)
      await fontFace.load()
    }
  },
  unregisterFont ({ commit, state }, { path, fontFamily }) {
    if (!path) {
      throw new Error('Can\'t unregister font. Path has to be provided.')
    }
    const dangling = commit('unregisterFont', { path, fontFamily })
    if (state.fontFaces[fontFamily] && dangling) {
      document.fonts.delete(state.fontFaces[fontFamily])
      commit('removeFontFace', { fontFamily })
    }
  }
}

const getters = {
  link: state => href => state.links,
  media: state => state.media,
  mediaPlayingCount: state => {
    let count = 0
    for (const player in state.media) {
      if (state.media[player]) { count++ }
    }
    return count
  },
  isAssetSelected: state => path => {
    return state.selectedAssets[path] === true
  },
  selectedAssets: state => state.selectedAssets,
  uiLocale: () => i18n.locale,
  contentLocale: state => state.contentLocale,
  localeMode: (state, getters) => getters.contentLocale !== ''
}

const store = new Vuex.Store({
  state: {
    uiLocale: 'de',
    contentLocale: '',
    editMode: false,
    editAllowed: false,
    links: {},
    actions: {},
    project: '',
    selectedAssets: {},
    registeredFonts: {},
    fontFaces: {},
    user: '',
    media: {}
  },
  getters,
  actions,
  mutations
})

export default store
